import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  TextField,
  Paper,
  Box,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import {
  fetchHtmlPageByName,
  fetchAllHtmlPageNames,
  saveHtmlPage,
  updateHtmlPage,
} from "../../services/htmlPageService";
import TinyMCE from "../admin/tinyMCE";

const EditHtmlPage = () => {
  const defaultData = {
    name: "",
    title: "",
    description: "",
    content: "",
  };

  // State to track the current page data and the original loaded data for change detection.
  const [pageData, setPageData] = useState(defaultData);
  const [originalPageData, setOriginalPageData] = useState(defaultData);

  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [pageNames, setPageNames] = useState([]);
  const [selectedPageName, setSelectedPageName] = useState("");
  const editorRef = useRef(null);

  // State for the custom confirmation dialog for unsaved changes.
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingPageName, setPendingPageName] = useState("");

  useEffect(() => {
    console.log("pageData updated:", pageData);
  }, [pageData]);

  useEffect(() => {
    const loadPageNames = async () => {
      try {
        setIsLoading(true);
        const { data } = await fetchAllHtmlPageNames();
        if (data && Array.isArray(data)) {
          setPageNames(data);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };
    loadPageNames();
  }, []);

  // When the selectedPageName changes, load that page's data.
  useEffect(() => {
    if (selectedPageName) {
      loadHtmlPage();
    }
  }, [selectedPageName]);

  // Compare current pageData with the original loaded data to determine if there are unsaved changes.
  const hasUnsavedChanges = () => {
    return JSON.stringify(pageData) !== JSON.stringify(originalPageData);
  };

  // Handle selection change from the dropdown.
  const handlePageSelection = (event) => {
    const newPageName = event.target.value;
    if (hasUnsavedChanges()) {
      // Instead of using window.confirm, open the custom confirmation dialog.
      setPendingPageName(newPageName);
      setConfirmDialogOpen(true);
    } else {
      setSelectedPageName(newPageName);
    }
  };

  // User confirms discarding unsaved changes.
  const handleConfirmYes = () => {
    setSelectedPageName(pendingPageName);
    setPendingPageName("");
    setConfirmDialogOpen(false);
  };

  // User cancels discarding unsaved changes.
  const handleConfirmNo = () => {
    setPendingPageName("");
    setConfirmDialogOpen(false);
  };

  // Load page data from the server and update both pageData and originalPageData.
  const loadHtmlPage = async () => {
    setIsLoading(true);
    try {
      const savedPage = await fetchHtmlPageByName(selectedPageName);
      console.log("API response (load):", savedPage);
      if (savedPage?.data) {
        setPageData(savedPage.data);
        setOriginalPageData(savedPage.data); // Reset unsaved changes tracking
        setDialogMessage("Page loaded successfully");
        setDialogOpen(false);
      } else {
        setDialogMessage("Page not found");
        setDialogOpen(true);
      }
    } catch (error) {
      console.error("Error loading page data:", error);
      setDialogMessage("Error loading page");
      setDialogOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Save the current page. After a successful save/update, reset the originalPageData.
  const handleSave = async () => {
    setIsLoading(true);
    try {
      console.log("Saving pageData:", pageData);
      let response;
      if (pageData.name) {
        response = await updateHtmlPage(pageData);
        console.log("API response (update):", response);
        setDialogMessage("Page updated successfully");
      } else {
        response = await saveHtmlPage(pageData);
        console.log("API response (save):", response);
        setDialogMessage("Page saved successfully");
      }
      // Update originalPageData so that there are no longer unsaved changes.
      setOriginalPageData(pageData);
    } catch (error) {
      console.error("Error saving page data:", error);
      setDialogMessage("Error saving page");
    } finally {
      setIsLoading(false);
      setDialogOpen(true);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Paper elevation={3} sx={{ p: 3, mx: "auto", my: 2, width: "80%" }}>
        <Typography variant="h4" gutterBottom>
          Edit HTML Page
        </Typography>

        <FormControl fullWidth margin="normal">
          <InputLabel>Select Page</InputLabel>
          <Select value={selectedPageName} onChange={handlePageSelection}>
            {pageNames.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Title"
          value={pageData.title}
          onChange={(e) =>
            setPageData((prev) => ({ ...prev, title: e.target.value }))
          }
          fullWidth
          margin="normal"
        />

        <TextField
          label="Description"
          value={pageData.description}
          onChange={(e) =>
            setPageData((prev) => ({ ...prev, description: e.target.value }))
          }
          fullWidth
          margin="normal"
          multiline
          rows={3}
        />

        <TinyMCE
          value={pageData.content}
          onChange={(content) => setPageData((prev) => ({ ...prev, content }))}
          editorRef={editorRef}
        />

        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button variant="contained" color="primary" onClick={handleSave}>
            Save
          </Button>
        </Box>
      </Paper>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
          <DialogContent>
            <Typography>{dialogMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>OK</Button>
          </DialogActions>
        </Dialog>

      <Dialog open={confirmDialogOpen} onClose={handleConfirmNo}>
        <DialogTitle>Discard Unsaved Changes?</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. Do you want to discard them and load the new page?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmNo} color="primary">
            No
          </Button>
          <Button onClick={handleConfirmYes} color="primary">
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditHtmlPage;
